import { type ReactNode, useMemo } from 'react';

import type { SaveProgressStats } from '@/components/settings/save-progress.utils';
import type { PreviewSaveStatus } from '@/components/settings/setting-preview.types';
import type { SettingChange, SettingChangeGroup } from '@/components/settings/settings-save.types';
import { SettingsSaveDialog } from '@/components/settings/settings-save-dialog';
import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';

import { RESET_FAULTED_GROUP_LABEL } from '../reset-faulted.constants';
import type { ResetFaultedPayload } from '../reset-faulted.types';

interface ResetFaultedSaveDialogProps {
  changes: SettingChange<ResetFaultedPayload>[];
  hasPendingSave: boolean;
  onCancelPending: () => void;
  onClose: () => void;
  onConfirm: () => void;
  onDiscard: (changeId: string) => void;
  open: boolean;
  progressStats: SaveProgressStats;
  saveComplete: boolean;
  saveProgressByChangeId: Record<string, PreviewSaveStatus>;
}

/**
 * Confirm dialog of the Reset Faulted pane: the shared settings save dialog with
 * one row per device (device → target mode) and per-device progress.
 */
export const ResetFaultedSaveDialog = ({
  changes,
  hasPendingSave,
  onCancelPending,
  onClose,
  onConfirm,
  onDiscard,
  open,
  progressStats,
  saveComplete,
  saveProgressByChangeId,
}: ResetFaultedSaveDialogProps): ReactNode => {
  const { isDisabled: disabledByPermissions, tooltipText: permissionTooltipText } =
    useActionPermission({ privilege: PRIVILEGES.writeCommands });

  const groups = useMemo<SettingChangeGroup<ResetFaultedPayload>[]>(
    () => [{ id: 'reset-faulted', label: RESET_FAULTED_GROUP_LABEL, changes }],
    [changes]
  );

  return (
    <SettingsSaveDialog
      groups={groups}
      hasPendingSave={hasPendingSave}
      onCancelPending={onCancelPending}
      onClose={onClose}
      onConfirm={onConfirm}
      onDiscard={onDiscard}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      open={open}
      permissionTooltipText={permissionTooltipText}
      progressStats={progressStats}
      saveComplete={saveComplete}
      saveDisabledByPermissions={disabledByPermissions}
      saveProgressByChangeId={saveProgressByChangeId}
    />
  );
};
